import {
	ApartmentOutlined,
	ArrowLeftOutlined,
	CaretDownOutlined,
	CheckCircleOutlined,
	FileOutlined,
	FolderOpenOutlined,
	HistoryOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Skeleton, Table, Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import ReportingFormDrawerV2 from "../components/Reporting/ReportingFormDrawerV2";
import SearchButton from "../components/SearchButton";
import { useLookup } from "../hooks/useLookup";
import useSearch from "../hooks/useSearch";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { getMyReportsDashboard } from "../services/api-server/my_reports";
import { useUserStore } from "../stores/userStore";
import {
	MyReportsTableData,
	MyReportsTableDataWithLookup,
	ReportCardData,
	ReportStatus,
} from "../types/my_reports.types";
import { TReportType } from "../types/reporting.types";
import { formatDateTime } from "../utils/date_formatter";
import { emailsEqual } from "../utils/utils";
import { createMyReportsLookupConfig } from "../utils/reporting/lookupConfigs/my_reports";
import { searchRecord } from "../utils/searchRecord";
import { renderTableCheckboxFilter } from "../components/Table/TableCheckboxFilter";

const TagColorMap = {
	au: "magenta",
	ir: "green",
	moc: "orange",
	ncr: "blue",
	ofi: "cyan",
};

const ReportCardStatusMap = {
	draft: {
		title: "Draft",
		icon: FileOutlined,
	},
	in_review: {
		title: "In review",
		icon: HistoryOutlined,
	},
	open: {
		title: "Open",
		icon: FolderOpenOutlined,
	},
	closed: {
		title: "Closed",
		icon: CheckCircleOutlined,
	},
};

const ReportTypeTextMap = {
	audit: "Audit",
	incident_report: "Incident Report (IR)",
	moc: "Management of Change (MOC)",
	ofi: "Opportunity for Improvement (OFI)",
	non_conformance: "Non-Conformance Report (NCR)",
};

const ReportTypeRouteMap = {
	audit: "/reporting/audit-stream",
	incident_report: "/reporting/incident-report-stream",
	moc: "/reporting/moc-stream",
	ofi: "/reporting/ofi-stream",
	non_conformance: "/reporting/non-conformance-stream",
};

const ReportTableStatusMap: Record<
	string,
	{ text: string; color: string; auditText?: string }
> = {
	draft: {
		text: "Draft",
		color: "#D9D9D9",
	},
	in_review: {
		text: "Initial review",
		color: "gold",
	},
	changes_required: {
		text: "Changes requested",
		color: "gold",
	},
	in_progress: {
		text: "Completing actions",
		auditText: "Completing findings",
		color: "cyan",
	},
	final_review: {
		text: "Final review",
		color: "cyan",
	},
	revision_required: {
		text: "Changes requested",
		color: "gold",
	},
	closed_approved: {
		text: "Approved and closed",
		color: "green",
	},
	closed_rejected: {
		text: "Rejected and closed",
		color: "red",
	},
};

interface MyReportsProps {
	onLastUpdated?: (dateTime: string) => void;
	searchValue?: string;
	sortOrder?: 1 | -1;
	showClosedReports?: boolean;
}

const MyReports = ({
	onLastUpdated,
	searchValue = "",
	sortOrder = -1,
	showClosedReports = false,
}: MyReportsProps) => {
	const navigate = useNavigate();
	const windowDimensions = useWindowDimensions();
	const [lastUpdatedOn, setLastUpdatedOn] = useState<string | null>(null);
	const [selectedReport, setSelectedReport] = useState<{
		reportId: string;
		reportType: TReportType;
	} | null>(null);

	const user = useUserStore((state) => state.user);

	// Table filtering
	const [searchRecords, setSearchRecords] = useState<Record<string, string[]>>(
		{}
	);

	const { data, isLoading, isFetching, refetch } = useQuery({
		queryKey: ["my_reports"],
		queryFn: async () => {
			const res = await getMyReportsDashboard(user.email);

			const processedStats = Object.entries(res.stats).map(([key, value]) => {
				const { title, icon } =
					ReportCardStatusMap[key as keyof typeof ReportCardStatusMap];
				return {
					title,
					icon,
					count: value.count,
					stats: value.stats,
				};
			});
			const formattedDate = formatDateTime(new Date().toISOString());
			setLastUpdatedOn(formattedDate);
			onLastUpdated?.(formattedDate);
			return {
				table_data: res.table_data,
				stats: processedStats,
			};
		},
		refetchOnMount: "always",
		staleTime: 5 * 60 * 1000,
	});

	const lookupConfig = useMemo(() => {
		const uniqueOperators = Array.isArray(data?.table_data)
			? [...new Set(data?.table_data.map((item: any) => item.operator))]
			: [];
		return createMyReportsLookupConfig(uniqueOperators);
	}, [data?.table_data]);

	const { dataWithLookup, isLoading: isLoadingLookup } = useLookup(
		["my_reports", "lookup"],
		Array.isArray(data?.table_data) ? data?.table_data : [],
		lookupConfig
	);

	const globalSearch = useCallback((data: any[], query: string) => {
		if (!query || typeof query !== "string") return data;
		const lowerQuery = query.toLowerCase();
		return data.filter((item: any) => {
			const fields = [
				item.report_number,
				item.title,
				item.report_number_and_title,
				item.operator,
				item.project_name,
				item.report_type,
				...(Array.isArray(item.assigned_to_details)
					? item.assigned_to_details.map((d: any) => d.name + " " + d.email)
					: []),
			];
			return fields.some((field) =>
				String(field ?? "")
					.toLowerCase()
					.includes(lowerQuery)
			);
		});
	}, []);

	const tableData = useSearch(
		dataWithLookup ?? data?.table_data ?? [],
		searchValue,
		globalSearch,
		searchRecord({ searchRecords }) as any
	);

	const sortedTableData = useMemo(() => {
		if (!tableData || !Array.isArray(tableData)) return tableData;
		let result = [...tableData];

		if (!showClosedReports) {
			result = result.filter(
				(item: any) =>
					!["closed_approved", "closed_rejected"].includes(item.combined_status)
			);
		}

		return result.sort((a: any, b: any) => {
			const dateA = a.submitted_on ? new Date(a.submitted_on).getTime() : null;
			const dateB = b.submitted_on ? new Date(b.submitted_on).getTime() : null;
			if (dateA && dateB) {
				return sortOrder === 1 ? dateA - dateB : dateB - dateA;
			} else if (dateA) {
				return sortOrder === 1 ? -1 : 1;
			} else if (dateB) {
				return sortOrder === 1 ? 1 : -1;
			}
			return 0;
		});
	}, [tableData, sortOrder, showClosedReports]);

	const { reportTypeFilters, statusFilters } = useMemo(() => {
		if (!Array.isArray(data?.table_data))
			return { reportTypeFilters: [], statusFilters: [] };
		const reportTypeFilters = Array.from(
			new Set(data?.table_data.map((item) => item.report_type))
		)
			.map((value: string) => ({
				text:
					ReportTypeTextMap[value as keyof typeof ReportTypeTextMap] ||
					value ||
					"Unknown",
				value: value,
			}))
			.filter((item) => item.value); // Filter out any undefined/null values
		const statusFilters = Array.from(
			new Set(data?.table_data.map((item) => item.combined_status))
		)
			.map((value: string) => ({
				text:
					ReportTableStatusMap[value as keyof typeof ReportTableStatusMap]
						?.text ||
					value ||
					"Unknown",
				value: value,
			}))
			.filter((item) => item.value); // Filter out any undefined/null values
		return { reportTypeFilters, statusFilters };
	}, [data?.table_data]);

	const assignedToFilters = useMemo(() => {
		if (!dataWithLookup || !Array.isArray(dataWithLookup)) return [];
		const filters = Array.from(
			new Set(
				dataWithLookup.flatMap((item) =>
					Array.isArray(item.assigned_to_details)
						? item.assigned_to_details.map((detail: any) => detail.email)
						: []
				)
			)
			// Filter out any undefined/null values
		)
			.filter((value: string) => value !== undefined && value !== null)
			.map((value: string) => ({
				text: value,
				value: value,
			}));
		return filters;
	}, [dataWithLookup]);

	const handleOpenReport = (
		reportId: string,
		reportType: TReportType,
		reportStatus: ReportStatus
	) => {
		try {
			if (["closed_approved", "closed_rejected"].includes(reportStatus)) {
				const route =
					ReportTypeRouteMap[reportType as keyof typeof ReportTypeRouteMap];
				if (!route) throw new Error("Invalid report type");
				navigate(`${route}/${reportId}`);
			} else {
				setSelectedReport({
					reportId,
					reportType,
				});
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleCloseDrawer = () => {
		setSelectedReport(null);
	};

	const columns: ColumnsType<
		MyReportsTableData | MyReportsTableDataWithLookup
	> = [
		{
			title: (
				<SearchButton
					handleSearch={(value: any) => {
						setSearchRecords((prev: any) => {
							return {
								...prev,
								report_number_and_title: [value.value],
							};
						});
					}}
					headerName="Report title & tenant"
					columnKey="report_number_and_title"
					key={"report_number_and_title"}
				/>
			),
			dataIndex: "report_number_and_title",
			key: "report_number_and_title",
			render: (value, record) => (
				<div className="p-4 flex flex-col gap-y-2 items-start">
					<Button
						type="link"
						className="p-0!"
						disabled={record.combined_status === "closed_rejected"}
						onClick={() =>
							handleOpenReport(
								record.key,
								record.report_type,
								record.combined_status
							)
						}
					>
						{value}
					</Button>
					<div className="flex gap-x-2 items-center text-gray-500">
						<ApartmentOutlined />
						<span>{record.operator}</span>
					</div>
				</div>
			),
		},
		{
			title: "Type",
			dataIndex: "report_type",
			key: "report_type",
			width: 200,
			filters: reportTypeFilters,
			filterDropdown: renderTableCheckboxFilter,
			onFilter: (value, record) => record.report_type === value,
			render: (value) => (
				<p>
					{ReportTypeTextMap[value as keyof typeof ReportTypeTextMap] ||
						value ||
						"Unknown"}
				</p>
			),
		},
		{
			title: (
				<SearchButton
					handleSearch={(value: any) => {
						setSearchRecords((prev: any) => {
							return {
								...prev,
								project_name: [value.value],
							};
						});
					}}
					headerName="Project"
					columnKey="project_name"
					key={"project_name"}
					disabled={isLoadingLookup}
				/>
			),
			dataIndex: "project_name",
			key: "project_name",
			width: 200,
			render: (value) => (
				<Skeleton
					loading={isLoadingLookup}
					active={true}
					paragraph={{ rows: 1 }}
				>
					<p>{value ?? "—"}</p>
				</Skeleton>
			),
		},
		{
			title: "Submitted on",
			dataIndex: "submitted_on",
			key: "submitted_on",
			width: 200,
			showSorterTooltip: false,
			sorter: (a, b) => {
				const dateA = a.submitted_on ? new Date(a.submitted_on) : null;
				const dateB = b.submitted_on ? new Date(b.submitted_on) : null;
				if (dateA && dateB) return dateA.getTime() - dateB.getTime();
				else if (dateA) return 1;
				else if (dateB) return -1;
				else return 0;
			},
			render: (value) => <p>{value ? formatDateTime(value) : "—"}</p>,
		},
		{
			title: "Status",
			dataIndex: "combined_status",
			key: "combined_status",
			filters: statusFilters,
			filterDropdown: renderTableCheckboxFilter,
			width: 200,
			onFilter: (value, record) => record.combined_status === value,
			render: (value, record) => {
				const statusInfo =
					ReportTableStatusMap[value as keyof typeof ReportTableStatusMap];
				const color = statusInfo?.color || "#FFFFFF";
				const text =
					(record.report_type === "audit"
						? statusInfo?.auditText
						: undefined) ||
					statusInfo?.text ||
					value ||
					"Unknown";
				return (
					<div className="flex gap-x-2 flex-wrap">
						<Badge color={color} text={text} />
						{value === "draft" && (
							<p className="text-gray-500">
								({formatDateTime(record.updated_at)})
							</p>
						)}
					</div>
				);
			},
		},
		{
			title: "Assigned to",
			dataIndex: "assigned_to_details",
			key: "assigned_to_details",
			width: 200,
			filters: assignedToFilters,
			filterDropdown: renderTableCheckboxFilter,
			onFilter: (value, record: any) =>
				record.assigned_to_details.some((detail: any) =>
					emailsEqual(detail.email, value)
				),
			render: (value, record) => (
				<Skeleton loading={isLoadingLookup} active paragraph={{ rows: 1 }}>
					{value?.length > 0 ? (
						<div className="flex flex-col gap-y-2">
							{value.map((item: any, index: number) => {
								const isAuthor = emailsEqual(
									item.email,
									record.primary_author || record.author
								);
								return (
									<p
										key={index}
										className={`${isAuthor && "text-[#FFAA33] cursor-pointer hover:text-[#FFAA33]/60 transition-colors duration-300"}`}
										onClick={() =>
											isAuthor &&
											handleOpenReport(
												record.key,
												record.report_type,
												record.combined_status
											)
										}
									>
										{item.name}{" "}
										<span className={`${!isAuthor && "text-gray-500"}`}>
											({item.email})
										</span>
									</p>
								);
							})}
						</div>
					) : (
						<p>—</p>
					)}
				</Skeleton>
			),
		},
	];

	return (
		<>
			<div className="flex flex-row! gap-4">
				<div className="flex flex-col flex-1 overflow-hidden">
					<Table
						rootClassName="custom-table"
						columns={columns}
						dataSource={sortedTableData}
						scroll={{ y: windowDimensions.height - 380, x: "max-content" }}
						loading={isLoading}
						pagination={{
							defaultPageSize: 20,
							pageSizeOptions: [20, 50, 100],
							showSizeChanger: true,
							position: ["bottomCenter"],
						}}
					/>
				</div>
				<div className="flex flex-col w-[265px] h-full overflow-y-auto gap-y-4">
					{data
						? data.stats.map((cardData, index) => (
								<CollapsibleCard key={index} cardData={cardData} />
							))
						: Array.from({ length: 4 }).map((_, index) => (
								<CollapsibleCard key={index} cardData={null} />
							))}
				</div>
			</div>
			<ReportingFormDrawerV2
				isOpen={!!selectedReport}
				reportType={selectedReport?.reportType as TReportType}
				activeId={selectedReport?.reportId}
				onClose={handleCloseDrawer}
			/>
		</>
	);
};

const CollapsibleCard = ({ cardData }: { cardData: ReportCardData | null }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const expandable = cardData && cardData.count > 0;
	const isDarkTheme = localStorage.getItem("theme") === "dark";

	const renderContent = () => {
		if (cardData) {
			const { title, icon: Icon, count, stats } = cardData;
			return (
				<>
					<div className="flex items-center gap-x-6">
						<div className="flex items-center justify-center w-12 h-12">
							<Icon className="text-2xl" />
						</div>
						<div className="flex flex-col">
							<p className="text-gray-500">{title}</p>
							<div className="flex items-center gap-x-2">
								<p className="font-bold text-4xl">{count}</p>
								{expandable && (
									<CaretDownOutlined
										className={`text-sm transform transition-transform duration-300 ease-in-out ${
											isExpanded ? "rotate-180" : ""
										}`}
									/>
								)}
							</div>
						</div>
					</div>
					<div
						className={`overflow-hidden transition-all duration-300 ease-in-out ${
							isExpanded ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0"
						}`}
					>
						<div className="flex flex-col gap-y-3">
							<p className="text-gray-500">By report type</p>
							{Object.entries(stats).map(([key, value], index) => {
								if (value === 0) return null;
								const color =
									TagColorMap[key as keyof typeof TagColorMap] || "default";
								return (
									<div className="flex gap-x-2" key={index}>
										<Tag className="w-12 text-center!" color={color}>
											{key.toUpperCase()}
										</Tag>
										<p>{value} reports</p>
									</div>
								);
							})}
						</div>
					</div>
				</>
			);
		} else {
			return <Skeleton active paragraph={{ rows: 1.5 }} />;
		}
	};

	return (
		<div
			className={`flex flex-col ${isDarkTheme ? "bg-linear-to-b from-[#000026] to-[#1A1344]" : "bg-(--project-cards-bg)"} py-4 px-6 rounded-lg select-none ${expandable && "cursor-pointer"}`}
			onClick={() => expandable && setIsExpanded((prev) => !prev)}
		>
			{renderContent()}
		</div>
	);
};

export default MyReports;
